import { useEffect, useRef } from "react";
import maplibregl, {
  type CustomLayerInterface,
  type FilterSpecification,
  type GeoJSONSource,
  type LngLatBounds,
  type StyleSpecification,
} from "maplibre-gl";
import type { FeatureCollection, Point } from "geojson";
import { computeMapLight } from "../lib/sun";
import type {
  BenchFeatureProperties,
  CafeFeatureProperties,
  CafePointFeature,
  FetchStatus,
  ShadowTimelineEntry,
  TreeFeatureProperties,
} from "../lib/types";
import { ALL_CATEGORIES, ALL_SUN, type CategoryValue, type SunValue } from "./CategoryFilter";
import { DEFAULT_SHADOW_CONFIG, GroundShadowSystem } from "../lib/shadow-gl";
import { getOpeningHoursStatus } from "../lib/opening-hours";
import cafeSvg from "../assets/cafe.svg" with { type: "text" };
import barSvg from "../assets/bar.svg" with { type: "text" };
import restaurantSvg from "../assets/restaurant.svg" with { type: "text" };
import benchBackrestSvg from "../assets/bench-backrest.svg" with { type: "text" };
import benchSvg from "../assets/bench.svg" with { type: "text" };
import libertyStyle from "../data/liberty-style.jsonc"; // from https://tiles.openfreemap.org/styles/liberty (https://github.com/hyperknot/openfreemap-styles/blob/main/styles/liberty/style.json)
import { fetchMapDataFromR2 } from "@/lib/r2-data";

export type SelectedCafe = {
  name: string;
  address: string;
  openingHours: string;
  outdoorSeating: string;
  category: string;
  countryCode?: string;
  state?: string;
  osmType: string;
  osmId: string;
  coordinates: [number, number];
  inShadow?: boolean;
};

export type SearchTarget = {
  center: [number, number];
  zoom: number;
};

type MapViewProps = {
  simulatedDate: Date;
  recenterRequestToken: number;
  searchTarget: SearchTarget | null;
  selectedCafeCoordinates: [number, number] | null;
  sunriseMinutes: number;
  sunsetMinutes: number;
  selectedCategories: Set<CategoryValue>;
  selectedSun: Set<SunValue>;
  openOnly: boolean;
  onFetchStatusChange: (status: FetchStatus, message: string) => void;
  onPlacesReady: (features: CafePointFeature[]) => void;
  onCafeSelect: (cafe: SelectedCafe | null) => void;
  onShadowTimeline: (timeline: ShadowTimelineEntry[] | null) => void;
  onRecenterRequestComplete: () => void;
  onSearchComplete: () => void;
};

const FALLBACK_CENTER: [number, number] = [13.405, 52.52];
const MAP_STYLE = libertyStyle as StyleSpecification;
const DEFAULT_CAMERA_ZOOM = 16;
const DEFAULT_CAMERA_BEARING = 0;
const DEFAULT_CAMERA_PITCH = 0;
const MIN_FETCH_ZOOM = 14;

const CAFE_SOURCE_ID = "cafes";
const CAFE_LAYER_ID = "cafes-circle";
const CAFE_LABEL_LAYER_ID = "cafes-label";
const CAFE_MARKER_SUN_IMAGE_ID = "cafes-marker-sun";
const CAFE_MARKER_SHADOW_IMAGE_ID = "cafes-marker-shadow";
const BAR_MARKER_SUN_IMAGE_ID = "bars-marker-sun";
const BAR_MARKER_SHADOW_IMAGE_ID = "bars-marker-shadow";
const RESTAURANT_MARKER_SUN_IMAGE_ID = "restaurants-marker-sun";
const RESTAURANT_MARKER_SHADOW_IMAGE_ID = "restaurants-marker-shadow";
const TREE_SOURCE_ID = "trees";
const TREE_LAYER_ID = "trees-circle";
const BENCH_SOURCE_ID = "benches";
const BENCH_LAYER_ID = "benches-symbol";
const BENCH_SUN_IMAGE_ID = "bench-sun-icon";
const BENCH_SUN_BACKREST_IMAGE_ID = "bench-sun-backrest-icon";
const BENCH_SHADOW_IMAGE_ID = "bench-shadow-icon";
const BENCH_SHADOW_BACKREST_IMAGE_ID = "bench-shadow-backrest-icon";
const SHADOW_LAYER_ID = "building-ground-shadows";

const EMPTY_POINTS: FeatureCollection<Point, CafeFeatureProperties> = {
  type: "FeatureCollection",
  features: [],
};
const EMPTY_TREES: FeatureCollection<Point, TreeFeatureProperties> = {
  type: "FeatureCollection",
  features: [],
};
const EMPTY_BENCHES: FeatureCollection<Point, BenchFeatureProperties> = {
  type: "FeatureCollection",
  features: [],
};
let isCafeMarkerLoading = false;
let isBenchIconLoading = false;

function getCategorySetFilter(cats: Set<CategoryValue>): FilterSpecification | undefined {
  if (cats.size === 0) {
    return ["literal", false];
  }
  if (cats.size === ALL_CATEGORIES.size) {
    return undefined;
  }
  return ["in", ["get", "category"], ["literal", [...cats]]];
}

function getSunSetFilter(sun: Set<SunValue>): FilterSpecification | undefined {
  if (sun.size === 0) {
    return ["literal", false];
  }
  if (sun.size === ALL_SUN.size) {
    return undefined;
  }
  if (sun.has("sun")) {
    return ["!=", ["get", "inShadow"], true];
  }
  return ["==", ["get", "inShadow"], true];
}

function getOpenFilter(openOnly: boolean): FilterSpecification | undefined {
  if (openOnly) {
    return ["==", ["get", "isOpen"], true];
  }
  return undefined;
}

/**
 * Annotate each cafe feature with an `isOpen` flag based on the opening hours
 * evaluated at the given date, so the map layer can be filtered by open state.
 */
function annotateOpenStatus(
  collection: FeatureCollection<Point, CafeFeatureProperties>,
  date: Date,
): FeatureCollection<Point, CafeFeatureProperties> {
  return {
    ...collection,
    features: collection.features.map(feature => {
      const status = getOpeningHoursStatus(feature.properties.openingHours, date, {
        coordinates: feature.geometry.coordinates as [number, number],
        countryCode: feature.properties.countryCode,
        state: feature.properties.state,
      });
      return {
        ...feature,
        properties: {
          ...feature.properties,
          isOpen: status.state === "open",
        },
      };
    }),
  };
}

function applyCafePresentation(
  map: maplibregl.Map,
  selectedCategories: Set<CategoryValue>,
  selectedSun: Set<SunValue>,
  openOnly: boolean,
): void {
  if (!map.getLayer(CAFE_LAYER_ID)) {
    return;
  }
  const catFilter = getCategorySetFilter(selectedCategories);
  const sunFilter = getSunSetFilter(selectedSun);
  const openFilter = getOpenFilter(openOnly);
  const activeFilters = [catFilter, sunFilter, openFilter].filter(
    (filter): filter is FilterSpecification => filter !== undefined,
  );
  const combined: FilterSpecification | undefined =
    activeFilters.length === 0
      ? undefined
      : activeFilters.length === 1
        ? activeFilters[0]
        : (["all", ...activeFilters] as FilterSpecification);
  map.setFilter(CAFE_LAYER_ID, combined);
  if (map.getLayer(CAFE_LABEL_LAYER_ID)) {
    map.setFilter(CAFE_LABEL_LAYER_ID, combined);
  }
}

function isAbortLikeError(error: unknown): boolean {
  if (!error) {
    return false;
  }
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  if (error instanceof Error) {
    return error.name === "AbortError" || /aborted/i.test(error.message);
  }
  if (typeof error === "object" && "name" in error && (error as { name?: unknown }).name === "AbortError") {
    return true;
  }
  if (typeof error === "string") {
    return /aborted/i.test(error);
  }
  return false;
}

function mapCenterLight(map: maplibregl.Map, simulatedDate: Date): void {
  if (!map.isStyleLoaded()) {
    return;
  }

  const center = map.getCenter();
  map.setLight(computeMapLight(simulatedDate, center.lat, center.lng));
}

function createTintedSvg(svg: string, color: string): string {
  return svg.replace("currentColor", color);
}

function createTintedBenchSvg(svg: string, color: string): string {
  return svg.replaceAll("#AAAAAA", color).replaceAll("#aaaaaa", color);
}

function loadSvgImageToMap(map: maplibregl.Map, id: string, svg: string, next: () => void): void {
  if (map.hasImage(id)) {
    next();
    return;
  }

  const image = new Image();
  image.onload = () => {
    const baseSize = Math.max(image.naturalWidth, image.naturalHeight, 1);
    const markerPixelRatio = Math.max(1, Math.min(2, Math.round(window.devicePixelRatio || 1)));
    const sizePx = baseSize * markerPixelRatio;
    const canvas = document.createElement("canvas");
    canvas.width = sizePx;
    canvas.height = sizePx;
    const context = canvas.getContext("2d");
    if (!context) {
      isBenchIconLoading = false;
      return;
    }

    context.clearRect(0, 0, sizePx, sizePx);
    context.drawImage(image, 0, 0, sizePx, sizePx);
    const imageData = context.getImageData(0, 0, sizePx, sizePx);
    if (!map.hasImage(id)) {
      map.addImage(id, imageData, { pixelRatio: markerPixelRatio });
    }
    next();
  };
  image.onerror = error => {
    console.error("Unable to load bench icon", error);
    isBenchIconLoading = false;
  };
  image.src = `data:image/svg+xml;base64,${btoa(svg)}`;
}

function loadCircleMarker(
  map: maplibregl.Map,
  id: string,
  svgSource: string,
  backgroundColor: string,
  iconColor: string,
  next: () => void,
): void {
  if (map.hasImage(id)) {
    next();
    return;
  }

  const iconDataUrl = `data:image/svg+xml;base64,${btoa(createTintedSvg(svgSource, iconColor))}`;
  const image = new Image();
  image.onload = () => {
    const logicalSize = 26;
    const logicalCircleRadius = 12;
    const logicalStrokeWidth = 2;
    const logicalIconSize = 14;
    const logicalIconInset = (logicalSize - logicalIconSize) / 2;
    const markerPixelRatio = Math.max(1, Math.min(2, Math.round(window.devicePixelRatio || 1)));
    const markerSizePx = logicalSize * markerPixelRatio;
    const canvas = document.createElement("canvas");
    canvas.width = markerSizePx;
    canvas.height = markerSizePx;
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    context.clearRect(0, 0, markerSizePx, markerSizePx);
    context.beginPath();
    context.arc(
      (logicalSize / 2) * markerPixelRatio,
      (logicalSize / 2) * markerPixelRatio,
      logicalCircleRadius * markerPixelRatio,
      0,
      Math.PI * 2,
    );
    context.fillStyle = backgroundColor;
    context.fill();
    context.lineWidth = logicalStrokeWidth * markerPixelRatio;
    context.strokeStyle = "#ffffff";
    context.stroke();
    context.drawImage(
      image,
      logicalIconInset * markerPixelRatio,
      logicalIconInset * markerPixelRatio,
      logicalIconSize * markerPixelRatio,
      logicalIconSize * markerPixelRatio,
    );

    const imageData = context.getImageData(0, 0, markerSizePx, markerSizePx);
    if (!map.hasImage(id)) {
      map.addImage(id, imageData, { pixelRatio: markerPixelRatio });
    }
    next();
  };
  image.onerror = error => {
    console.error(`Unable to load marker icon: ${id}`, error);
  };
  image.src = iconDataUrl;
}

function ensureAllMarkerImages(
  map: maplibregl.Map,
  onDone: () => void,
): void {
  const allPresent =
    map.hasImage(CAFE_MARKER_SUN_IMAGE_ID) &&
    map.hasImage(CAFE_MARKER_SHADOW_IMAGE_ID) &&
    map.hasImage(BAR_MARKER_SUN_IMAGE_ID) &&
    map.hasImage(BAR_MARKER_SHADOW_IMAGE_ID) &&
    map.hasImage(RESTAURANT_MARKER_SUN_IMAGE_ID) &&
    map.hasImage(RESTAURANT_MARKER_SHADOW_IMAGE_ID);

  if (allPresent) {
    onDone();
    return;
  }

  if (isCafeMarkerLoading) {
    return;
  }

  isCafeMarkerLoading = true;

  loadCircleMarker(map, CAFE_MARKER_SUN_IMAGE_ID, cafeSvg, "#fe9a00", "#f8fafc", () => {
    loadCircleMarker(map, CAFE_MARKER_SHADOW_IMAGE_ID, cafeSvg, "#90a1b9", "#f8fafc", () => {
      loadCircleMarker(map, BAR_MARKER_SUN_IMAGE_ID, barSvg, "#fe9a00", "#f8fafc", () => {
        loadCircleMarker(map, BAR_MARKER_SHADOW_IMAGE_ID, barSvg, "#90a1b9", "#f8fafc", () => {
          loadCircleMarker(map, RESTAURANT_MARKER_SUN_IMAGE_ID, restaurantSvg, "#fe9a00", "#f8fafc", () => {
            loadCircleMarker(map, RESTAURANT_MARKER_SHADOW_IMAGE_ID, restaurantSvg, "#90a1b9", "#f8fafc", () => {
              isCafeMarkerLoading = false;
              onDone();
            });
          });
        });
      });
    });
  });
}

function ensureBenchImages(map: maplibregl.Map, onDone: () => void): void {
  if (
    map.hasImage(BENCH_SUN_IMAGE_ID) &&
    map.hasImage(BENCH_SUN_BACKREST_IMAGE_ID) &&
    map.hasImage(BENCH_SHADOW_IMAGE_ID) &&
    map.hasImage(BENCH_SHADOW_BACKREST_IMAGE_ID)
  ) {
    onDone();
    return;
  }

  if (isBenchIconLoading) {
    return;
  }

  isBenchIconLoading = true;
  const sunColor = "#f59e0b";
  const shadowColor = "#90a1b9";
  loadSvgImageToMap(map, BENCH_SUN_IMAGE_ID, createTintedBenchSvg(benchSvg, sunColor), () => {
    loadSvgImageToMap(
      map,
      BENCH_SUN_BACKREST_IMAGE_ID,
      createTintedBenchSvg(benchBackrestSvg, sunColor),
      () => {
        loadSvgImageToMap(map, BENCH_SHADOW_IMAGE_ID, createTintedBenchSvg(benchSvg, shadowColor), () => {
          loadSvgImageToMap(
            map,
            BENCH_SHADOW_BACKREST_IMAGE_ID,
            createTintedBenchSvg(benchBackrestSvg, shadowColor),
            () => {
              isBenchIconLoading = false;
              onDone();
            },
          );
        });
      },
    );
  });
}

function addCafeMarkerLayer(map: maplibregl.Map): void {
  if (map.getLayer(CAFE_LAYER_ID)) {
    return;
  }

  map.addLayer({
    id: CAFE_LAYER_ID,
    type: "symbol",
    source: CAFE_SOURCE_ID,
    layout: {
      "icon-image": [
        "case",
        ["all", ["==", ["get", "category"], "bar"], ["==", ["get", "inShadow"], true]],
        BAR_MARKER_SHADOW_IMAGE_ID,
        ["==", ["get", "category"], "bar"],
        BAR_MARKER_SUN_IMAGE_ID,
        ["all", ["==", ["get", "category"], "restaurant"], ["==", ["get", "inShadow"], true]],
        RESTAURANT_MARKER_SHADOW_IMAGE_ID,
        ["==", ["get", "category"], "restaurant"],
        RESTAURANT_MARKER_SUN_IMAGE_ID,
        ["==", ["get", "inShadow"], true],
        CAFE_MARKER_SHADOW_IMAGE_ID,
        CAFE_MARKER_SUN_IMAGE_ID,
      ],
      "icon-size": 1,
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
  });
}

function addBenchLayer(map: maplibregl.Map): void {
  if (map.getLayer(BENCH_LAYER_ID)) {
    if (map.getLayer(CAFE_LAYER_ID)) {
      map.moveLayer(BENCH_LAYER_ID, CAFE_LAYER_ID);
    }
    return;
  }

  map.addLayer({
    id: BENCH_LAYER_ID,
    type: "symbol",
    source: BENCH_SOURCE_ID,
    minzoom: MIN_FETCH_ZOOM,
    layout: {
      "icon-image": [
        "case",
        ["==", ["get", "inShadow"], true],
        [
          "match",
          ["downcase", ["to-string", ["coalesce", ["get", "backrest"], ""]]],
          "yes",
          BENCH_SHADOW_BACKREST_IMAGE_ID,
          "true",
          BENCH_SHADOW_BACKREST_IMAGE_ID,
          "1",
          BENCH_SHADOW_BACKREST_IMAGE_ID,
          BENCH_SHADOW_IMAGE_ID,
        ],
        [
          "match",
          ["downcase", ["to-string", ["coalesce", ["get", "backrest"], ""]]],
          "yes",
          BENCH_SUN_BACKREST_IMAGE_ID,
          "true",
          BENCH_SUN_BACKREST_IMAGE_ID,
          "1",
          BENCH_SUN_BACKREST_IMAGE_ID,
          BENCH_SUN_IMAGE_ID,
        ],
      ],
      "icon-size": 0.9,
      "icon-allow-overlap": false,
      "icon-ignore-placement": false,
    },
    paint: {
      "icon-opacity": 0.8,
    },
  }, map.getLayer(CAFE_LAYER_ID) ? CAFE_LAYER_ID : undefined);
}

function upsertCafeLayer(map: maplibregl.Map, data: FeatureCollection<Point, CafeFeatureProperties>): void {
  if (!map.isStyleLoaded()) {
    return;
  }

  const existingSource = map.getSource(CAFE_SOURCE_ID);
  if (!existingSource) {
    map.addSource(CAFE_SOURCE_ID, {
      type: "geojson",
      data,
    });
  } else {
    (existingSource as GeoJSONSource).setData(data);
  }

  ensureAllMarkerImages(map, () => {
    addCafeMarkerLayer(map);
  });

  if (!map.getLayer(CAFE_LABEL_LAYER_ID)) {
    map.addLayer({
      id: CAFE_LABEL_LAYER_ID,
      type: "symbol",
      source: CAFE_SOURCE_ID,
      minzoom: 16,
      layout: {
        "text-field": ["coalesce", ["get", "name"], ""],
        "text-size": 12,
        "text-font": ["Noto Sans Regular"],
        "text-offset": [0, 1.5],
        "text-anchor": "top",
      },
      paint: {
        "text-color": "#0f172a",
        "text-halo-color": "#ffffff",
        "text-halo-width": 1.2,
      },
    });
  }
}

function upsertBenchLayer(map: maplibregl.Map, data: FeatureCollection<Point, BenchFeatureProperties>): void {
  const existingSource = map.getSource(BENCH_SOURCE_ID);
  if (!existingSource) {
    map.addSource(BENCH_SOURCE_ID, {
      type: "geojson",
      data,
    });
  } else {
    (existingSource as GeoJSONSource).setData(data);
  }

  ensureBenchImages(map, () => {
    addBenchLayer(map);
  });
}

function upsertTreeLayer(map: maplibregl.Map, data: FeatureCollection<Point, TreeFeatureProperties>): void {
  const existingSource = map.getSource(TREE_SOURCE_ID);
  if (!existingSource) {
    map.addSource(TREE_SOURCE_ID, {
      type: "geojson",
      data,
    });
  } else {
    (existingSource as GeoJSONSource).setData(data);
  }

  if (!map.getLayer(TREE_LAYER_ID)) {
    const treeLayer: maplibregl.CircleLayerSpecification = {
      id: TREE_LAYER_ID,
      type: "circle",
      source: TREE_SOURCE_ID,
      minzoom: 16,
      paint: {
        "circle-radius": [
          "match",
          ["%", ["get", "osmId"], 5],
          0, 2.4,
          1, 2.8,
          2, 3.1,
          3, 3.4,
          3.7,
        ],
        "circle-color": [
          "match",
          ["%", ["get", "osmId"], 6],
          0, "#15803d",
          1, "#166534",
          2, "#16a34a",
          3, "#22c55e",
          4, "#4ade80",
          "#2f9e44",
        ],
        "circle-opacity": 0.25,
      },
    };
    const beforeLayerId = map.getLayer(BENCH_LAYER_ID)
      ? BENCH_LAYER_ID
      : map.getLayer(CAFE_LAYER_ID)
        ? CAFE_LAYER_ID
        : undefined;
    map.addLayer(treeLayer, beforeLayerId);
  }
}

function createCurrentLocationMarkerElement(): HTMLDivElement {
  const container = document.createElement("div");
  container.style.width = "24px";
  container.style.height = "24px";
  container.style.display = "flex";
  container.style.alignItems = "center";
  container.style.justifyContent = "center";
  container.style.pointerEvents = "none";

  const halo = document.createElement("div");
  halo.style.position = "absolute";
  halo.style.width = "24px";
  halo.style.height = "24px";
  halo.style.borderRadius = "9999px";
  halo.style.backgroundColor = "rgba(59, 130, 246, 0.22)";

  const dot = document.createElement("div");
  dot.style.position = "absolute";
  dot.style.width = "12px";
  dot.style.height = "12px";
  dot.style.borderRadius = "9999px";
  dot.style.backgroundColor = "#2563eb";
  dot.style.border = "2px solid #ffffff";
  dot.style.boxSizing = "border-box";

  container.append(halo, dot);
  return container;
}

export function MapView({
  simulatedDate,
  recenterRequestToken,
  searchTarget,
  selectedCafeCoordinates,
  sunriseMinutes,
  sunsetMinutes,
  selectedCategories,
  selectedSun,
  openOnly,
  onFetchStatusChange,
  onPlacesReady,
  onCafeSelect,
  onShadowTimeline,
  onRecenterRequestComplete,
  onSearchComplete,
}: MapViewProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const inFlightBoundsKeyRef = useRef<string | null>(null);
  const lastCompletedBoundsKeyRef = useRef<string | null>(null);
  const latestSimulatedDateRef = useRef(simulatedDate);
  const shadowSystemRef = useRef<GroundShadowSystem | null>(null);
  const latestCafeDataRef = useRef<FeatureCollection<Point, CafeFeatureProperties>>(EMPTY_POINTS);
  const latestBenchDataRef = useRef<FeatureCollection<Point, BenchFeatureProperties>>(EMPTY_BENCHES);
  const latestLocationRef = useRef<[number, number] | null>(null);
  const currentLocationMarkerRef = useRef<maplibregl.Marker | null>(null);
  const selectedCategoriesRef = useRef(selectedCategories);
  const selectedSunRef = useRef(selectedSun);
  const openOnlyRef = useRef(openOnly);
  const simulatedDateRef = useRef(simulatedDate);
  selectedCategoriesRef.current = selectedCategories;
  selectedSunRef.current = selectedSun;
  openOnlyRef.current = openOnly;
  simulatedDateRef.current = simulatedDate;

  const getShadowBeforeLayerId = (map: maplibregl.Map): string | undefined => {
    const layers = map.getStyle().layers ?? [];
    return layers.find(layer => layer.type === "fill-extrusion")?.id ?? layers.find(layer => layer.type === "symbol")?.id;
  };

  const enforceOpaqueBuildings = (map: maplibregl.Map): void => {
    const layers = map.getStyle().layers ?? [];
    for (const layer of layers) {
      if (layer.type !== "fill-extrusion") {
        continue;
      }
      map.setPaintProperty(layer.id, "fill-extrusion-opacity", 1);
      map.setPaintProperty(layer.id, "fill-extrusion-color", "#ffffff");
    }
  };

  const toBoundsKey = (bounds: LngLatBounds): string => {
    const s = bounds.getSouth().toFixed(5);
    const w = bounds.getWest().toFixed(5);
    const n = bounds.getNorth().toFixed(5);
    const e = bounds.getEast().toFixed(5);
    const z = mapRef.current?.getZoom().toFixed(2) ?? "0";
    return `${s},${w},${n},${e},z${z}`;
  };

  const centerMapFromPosition = (
    position: GeolocationPosition,
    options?: { resetCamera?: boolean },
  ): void => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    const coordinates: [number, number] = [position.coords.longitude, position.coords.latitude];
    latestLocationRef.current = coordinates;
    if (!currentLocationMarkerRef.current) {
      currentLocationMarkerRef.current = new maplibregl.Marker({
        element: createCurrentLocationMarkerElement(),
        anchor: "center",
      }).setLngLat(coordinates).addTo(map);
    } else {
      currentLocationMarkerRef.current.setLngLat(coordinates);
    }

    const shouldResetCamera = options?.resetCamera ?? false;
    map.jumpTo(
      shouldResetCamera
        ? {
            center: coordinates,
            zoom: DEFAULT_CAMERA_ZOOM,
            bearing: DEFAULT_CAMERA_BEARING,
            pitch: DEFAULT_CAMERA_PITCH,
          }
        : {
            center: coordinates,
          },
    );
    mapCenterLight(map, latestSimulatedDateRef.current);
    shadowSystemRef.current?.setSunTime(latestSimulatedDateRef.current);
    shadowSystemRef.current?.invalidateGeometry();
    map.triggerRepaint();
  };

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) {
      return;
    }

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE,
      attributionControl: false,
      center: FALLBACK_CENTER,
      zoom: DEFAULT_CAMERA_ZOOM,
      maxZoom: 17,
      minZoom: 6, // zooming out further causes an error
      pitch: DEFAULT_CAMERA_PITCH,
      bearing: DEFAULT_CAMERA_BEARING,
    });

    mapRef.current = map;

    const refreshCafes = async (bounds: LngLatBounds): Promise<void> => {
      if (!map.isStyleLoaded()) {
        map.once("idle", () => {
          void refreshCafes(bounds);
        });
        return;
      }

      const boundsKey = toBoundsKey(bounds);
      if (map.getZoom() < MIN_FETCH_ZOOM) {
        latestCafeDataRef.current = EMPTY_POINTS;
        latestBenchDataRef.current = EMPTY_BENCHES;
        upsertCafeLayer(map, EMPTY_POINTS);
        upsertBenchLayer(map, EMPTY_BENCHES);
        upsertTreeLayer(map, EMPTY_TREES);
        applyCafePresentation(map, selectedCategoriesRef.current, selectedSunRef.current, openOnlyRef.current);
        onPlacesReady([]);
        onFetchStatusChange("idle", `Zoom in to ${MIN_FETCH_ZOOM}+ to load cafes`);
        inFlightBoundsKeyRef.current = null;
        lastCompletedBoundsKeyRef.current = null;
        return;
      }

      if (
        inFlightBoundsKeyRef.current === boundsKey ||
        lastCompletedBoundsKeyRef.current === boundsKey
      ) {
        return;
      }

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      inFlightBoundsKeyRef.current = boundsKey;

      onFetchStatusChange("loading", "Loading cafes...");

      try {
        const mapData = await fetchMapDataFromR2(bounds);
        if (controller.signal.aborted) {
          return;
        }
        latestCafeDataRef.current = mapData.cafes;
        latestBenchDataRef.current = mapData.benches;
        const cafesWithShadow = annotateOpenStatus(
          shadowSystemRef.current?.classifyCafeShadows(latestCafeDataRef.current) ?? mapData.cafes,
          simulatedDateRef.current,
        );
        const benchesWithShadow =
          shadowSystemRef.current?.classifyBenchShadows(latestBenchDataRef.current) ?? mapData.benches;
        latestCafeDataRef.current = cafesWithShadow;
        latestBenchDataRef.current = benchesWithShadow;
        upsertCafeLayer(map, cafesWithShadow);
        upsertBenchLayer(map, benchesWithShadow);
        upsertTreeLayer(map, mapData.trees);
        applyCafePresentation(map, selectedCategoriesRef.current, selectedSunRef.current, openOnlyRef.current);
        onPlacesReady(cafesWithShadow.features);
        const shadedCount = cafesWithShadow.features.filter(feature => feature.properties.inShadow).length;
        onFetchStatusChange(
          "ready",
          `${cafesWithShadow.features.length} places (${shadedCount} shaded), ${mapData.benches.features.length} benches, and ${mapData.trees.features.length} trees loaded`,
        );
        lastCompletedBoundsKeyRef.current = boundsKey;
        const needsSettledShadowClassification = cafesWithShadow.features.every(
          feature => !feature.properties.inShadow,
        );
        if (needsSettledShadowClassification) {
          map.once("idle", () => {
            if (controller.signal.aborted || lastCompletedBoundsKeyRef.current !== boundsKey) {
              return;
            }
            const settledCafesWithShadow = annotateOpenStatus(
              shadowSystemRef.current?.classifyCafeShadows(latestCafeDataRef.current) ?? latestCafeDataRef.current,
              simulatedDateRef.current,
            );
            const settledBenchesWithShadow =
              shadowSystemRef.current?.classifyBenchShadows(latestBenchDataRef.current) ?? latestBenchDataRef.current;
            latestCafeDataRef.current = settledCafesWithShadow;
            latestBenchDataRef.current = settledBenchesWithShadow;
            upsertCafeLayer(map, settledCafesWithShadow);
            upsertBenchLayer(map, settledBenchesWithShadow);
            applyCafePresentation(map, selectedCategoriesRef.current, selectedSunRef.current, openOnlyRef.current);
            onPlacesReady(settledCafesWithShadow.features);
            const settledShadedCount = settledCafesWithShadow.features.filter(feature => feature.properties.inShadow).length;
            onFetchStatusChange(
              "ready",
              `${settledCafesWithShadow.features.length} places (${settledShadedCount} shaded), ${mapData.benches.features.length} benches, and ${mapData.trees.features.length} trees loaded`,
            );
          });
        }
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        const message = error instanceof Error ? error.message : "Unknown Overpass error";
        onFetchStatusChange("error", message);
      } finally {
        if (inFlightBoundsKeyRef.current === boundsKey) {
          inFlightBoundsKeyRef.current = null;
        }
      }
    };

    const scheduleCafeRefresh = (): void => {
      void refreshCafes(map.getBounds());
    };

    const registerShadowLayer = (): void => {
      const shadowLayer: CustomLayerInterface = {
        id: SHADOW_LAYER_ID,
        type: "custom",
        renderingMode: "2d",
        onAdd: (_layerMap, gl) => {
          try {
            const shadowSystem = new GroundShadowSystem(map, gl, DEFAULT_SHADOW_CONFIG);
            shadowSystem.init();
            shadowSystem.setSunTime(latestSimulatedDateRef.current);
            shadowSystem.invalidateGeometry();
            shadowSystemRef.current = shadowSystem;
          } catch (error) {
            console.error("Shadow init failed", error);
            shadowSystemRef.current = null;
          }
        },
        render: (_gl, input) => {
          shadowSystemRef.current?.render(input);
        },
        onRemove: () => {
          shadowSystemRef.current?.dispose();
          shadowSystemRef.current = null;
        },
      };

      const beforeId = getShadowBeforeLayerId(map);
      map.addLayer(shadowLayer, beforeId);
      map.triggerRepaint();
    };

    map.on("load", () => {
      try {
        enforceOpaqueBuildings(map);
        registerShadowLayer();
      } catch (error) {
        console.error("Unable to add shadow layer", error);
      }

      upsertCafeLayer(map, EMPTY_POINTS);
      upsertBenchLayer(map, EMPTY_BENCHES);
      upsertTreeLayer(map, EMPTY_TREES);
      applyCafePresentation(map, selectedCategoriesRef.current, selectedSunRef.current, openOnlyRef.current);
      mapCenterLight(map, latestSimulatedDateRef.current);
      shadowSystemRef.current?.setSunTime(latestSimulatedDateRef.current);
      map.triggerRepaint();
      map.once("idle", () => {
        mapCenterLight(map, latestSimulatedDateRef.current);
        shadowSystemRef.current?.setSunTime(latestSimulatedDateRef.current);
        map.triggerRepaint();
        scheduleCafeRefresh();
      });
    });

    map.on("styledata", () => {
      if (!map.isStyleLoaded()) {
        return;
      }
      enforceOpaqueBuildings(map);
      mapCenterLight(map, latestSimulatedDateRef.current);
      shadowSystemRef.current?.setSunTime(latestSimulatedDateRef.current);
      map.triggerRepaint();
    });

    map.on("move", () => {
      mapCenterLight(map, latestSimulatedDateRef.current);
      shadowSystemRef.current?.setSunTime(latestSimulatedDateRef.current);
      shadowSystemRef.current?.invalidateGeometry();
      map.triggerRepaint();
    });

    map.on("moveend", () => {
      mapCenterLight(map, latestSimulatedDateRef.current);
      shadowSystemRef.current?.setSunTime(latestSimulatedDateRef.current);
      shadowSystemRef.current?.invalidateGeometry();
      map.triggerRepaint();
      scheduleCafeRefresh();
    });

    map.on("sourcedata", () => {
      shadowSystemRef.current?.invalidateGeometry();
      map.triggerRepaint();
    });

    map.on("error", event => {
      // Rapid pan/zoom cancels stale tile requests; treat those AbortErrors as expected noise.
      if (isAbortLikeError(event.error)) {
        event.preventDefault();
      }
    });

    const onCafeClick = (event: maplibregl.MapLayerMouseEvent) => {
      const clickedFeature = event.features?.[0];
      if (!clickedFeature || clickedFeature.geometry.type !== "Point") {
        return;
      }

      const properties = clickedFeature.properties as {
        name?: string;
        address?: string;
        openingHours?: string;
        outdoorSeating?: string;
        category?: string;
        countryCode?: string;
        state?: string;
        osmType?: string;
        osmId?: string;
        inShadow?: boolean;
      };

      const coordinates = [...clickedFeature.geometry.coordinates] as [number, number];
      const category = properties.category ?? "cafe";

      onCafeSelect({
        name:
          properties.name ??
          (category === "bar"
            ? "Unnamed bar"
            : category === "restaurant"
              ? "Unnamed restaurant"
              : "Unnamed cafe"),
        address: properties.address ?? "",
        openingHours: properties.openingHours ?? "",
        outdoorSeating: properties.outdoorSeating ?? "unknown",
        category,
        countryCode: properties.countryCode,
        state: properties.state,
        osmType: properties.osmType ?? "?",
        osmId: properties.osmId ?? "?",
        coordinates,
        inShadow: properties.inShadow,
      });
    };

    map.on("click", CAFE_LAYER_ID, onCafeClick);
    map.on("click", CAFE_LABEL_LAYER_ID, onCafeClick);

    const onCafeMouseEnter = () => {
      map.getCanvas().style.cursor = "pointer";
    };

    const onCafeMouseLeave = () => {
      map.getCanvas().style.cursor = "";
    };

    map.on("mouseenter", CAFE_LAYER_ID, onCafeMouseEnter);
    map.on("mouseenter", CAFE_LABEL_LAYER_ID, onCafeMouseEnter);
    map.on("mouseleave", CAFE_LAYER_ID, onCafeMouseLeave);
    map.on("mouseleave", CAFE_LABEL_LAYER_ID, onCafeMouseLeave);

    if (navigator.geolocation) {
      let hasCenteredFromLocation = false;

      navigator.geolocation.getCurrentPosition(
        position => {
          hasCenteredFromLocation = true;
          centerMapFromPosition(position);
        },
        () => {
          onFetchStatusChange("idle", "Using fallback city (Berlin)");
        },
        {
          enableHighAccuracy: false,
          maximumAge: 600_000,
          timeout: 4_000,
        },
      );

      navigator.geolocation.getCurrentPosition(
        position => {
          if (hasCenteredFromLocation) {
            return;
          }
          centerMapFromPosition(position);
        },
        () => {
          // No-op: fallback message handled by the fast attempt above.
        },
        {
          enableHighAccuracy: true,
          maximumAge: 10_000,
          timeout: 6_000,
        },
      );
    }

    return () => {
      abortControllerRef.current?.abort();
      shadowSystemRef.current?.dispose();
      shadowSystemRef.current = null;
      currentLocationMarkerRef.current?.remove();
      currentLocationMarkerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, [onPlacesReady, onFetchStatusChange, onCafeSelect]);

  useEffect(() => {
    latestSimulatedDateRef.current = simulatedDate;

    const map = mapRef.current;
    if (!map) {
      return;
    }
    if (!map.isStyleLoaded()) {
      return;
    }

    mapCenterLight(map, latestSimulatedDateRef.current);
    shadowSystemRef.current?.setSunTime(latestSimulatedDateRef.current);
    const cafesWithShadow = annotateOpenStatus(
      shadowSystemRef.current?.classifyCafeShadows(latestCafeDataRef.current) ?? latestCafeDataRef.current,
      simulatedDate,
    );
    const benchesWithShadow =
      shadowSystemRef.current?.classifyBenchShadows(latestBenchDataRef.current) ?? latestBenchDataRef.current;
    latestCafeDataRef.current = cafesWithShadow;
    latestBenchDataRef.current = benchesWithShadow;
    upsertCafeLayer(map, cafesWithShadow);
    upsertBenchLayer(map, benchesWithShadow);
    onPlacesReady(cafesWithShadow.features);

    applyCafePresentation(map, selectedCategoriesRef.current, selectedSunRef.current, openOnlyRef.current);
    map.triggerRepaint();
  }, [simulatedDate, onPlacesReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) {
      return;
    }
    applyCafePresentation(map, selectedCategories, selectedSun, openOnly);
    map.triggerRepaint();
  }, [selectedCategories, selectedSun, openOnly]);

  useEffect(() => {
    if (!searchTarget) return;
    const map = mapRef.current;
    if (!map) return;

    map.flyTo({
      center: searchTarget.center,
      zoom: Math.min(searchTarget.zoom, 17),
      bearing: DEFAULT_CAMERA_BEARING,
      pitch: DEFAULT_CAMERA_PITCH,
      speed: 1.4,
    });
    onSearchComplete();
  }, [searchTarget, onSearchComplete]);

  useEffect(() => {
    if (!selectedCafeCoordinates) {
      onShadowTimeline(null);
      return;
    }
    const map = mapRef.current;
    if (!map) return;

    map.flyTo({
      center: selectedCafeCoordinates,
      zoom: Math.min(Math.max(map.getZoom(), DEFAULT_CAMERA_ZOOM), 17),
      speed: 1.4,
    });

    const computeTimeline = () => {
      const shadow = shadowSystemRef.current;
      if (!shadow) return;
      const timeline = shadow.computeShadowTimeline(
        selectedCafeCoordinates,
        latestSimulatedDateRef.current,
        sunriseMinutes,
        sunsetMinutes,
      );
      onShadowTimeline(timeline.length > 0 ? timeline : null);
    };

    map.once("idle", computeTimeline);
  }, [selectedCafeCoordinates, sunriseMinutes, sunsetMinutes, onShadowTimeline]);

  useEffect(() => {
    if (recenterRequestToken === 0) {
      return;
    }
    if (!navigator.geolocation) {
      onFetchStatusChange("error", "Geolocation is not supported on this device");
      onRecenterRequestComplete();
      return;
    }

    let isCancelled = false;
    let isCompleted = false;
    let failureCount = 0;
    let lastErrorMessage = "Unable to get current location";

    const completeSuccess = (position: GeolocationPosition): void => {
      if (isCancelled || isCompleted) {
        return;
      }
      isCompleted = true;
      centerMapFromPosition(position, { resetCamera: true });
      onRecenterRequestComplete();
    };

    const completeError = (message: string): void => {
      if (isCancelled || isCompleted) {
        return;
      }
      isCompleted = true;
      onFetchStatusChange("error", message);
      onRecenterRequestComplete();
    };

    const handleFailure = (error: GeolocationPositionError): void => {
      if (isCancelled || isCompleted) {
        return;
      }

      lastErrorMessage = error.message || lastErrorMessage;
      failureCount += 1;

      // Permission-denied won't improve with retries in this interaction.
      if (error.code === error.PERMISSION_DENIED) {
        completeError(lastErrorMessage);
        return;
      }

      if (failureCount >= 2) {
        completeError(lastErrorMessage);
      }
    };

    // Fast coarse attempt (often available from cell/Wi-Fi quickly).
    navigator.geolocation.getCurrentPosition(completeSuccess, handleFailure, {
      enableHighAccuracy: false,
      maximumAge: 120_000,
      timeout: 3_000,
    });

    // More precise fallback with a longer timeout for slower GPS locks.
    navigator.geolocation.getCurrentPosition(completeSuccess, handleFailure, {
      enableHighAccuracy: true,
      maximumAge: 15_000,
      timeout: 12_000,
    });

    return () => {
      isCancelled = true;
    };
  }, [recenterRequestToken, onFetchStatusChange, onRecenterRequestComplete]);

  return <div ref={mapContainerRef} className="h-full w-full" />;
}
